/**
 * @lines98/client/lib/auth-flow — ЕДИНЫЙ вход/выход аккаунта (account-profile-
 * redesign.md §4.1 / §3.1).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки» + spec §4.1):
 * поверхность входа открывается из ТРЁХ мест (пилюля рейла / CTA гостевого Профиля /
 * re-login после ревока), а выход — из меню account-chip. Чтобы это НЕ было копией
 * проводки сервисов по экранам, вся обвязка (auth-modal + oauth-login/local-auth +
 * OAuth leave-guard + сигнал смены identity) собрана в ОДИН модуль. Все вызывающие
 * (app.ts, profile-screen) зовут `openLoginFlow()` / `logout()` — без дублей.
 *
 * SCOPE: `openLoginFlow()` — открыть auth-modal со всей проводкой (§4); `logout()` —
 * ОДНА общая logout-функция (§3.1, не деструктивна — re-mint анона). Обе на успехе
 * шлют `emitIdentityChanged()` → подписчики (account-slot рейла, открытый Профиль)
 * reconcile'ятся (§2.3 / §9).
 *
 * NOT IN SCOPE: рендер модалки (lib/auth-modal), сами сервисы (oauth-login/local-auth),
 * подписка на сигнал (identity-events; подписчики — рейл/Профиль).
 *
 * Cross-references:
 *   - account-profile-redesign.md §3.1 (выход), §4 (модалка), §4.1 (3 вызова), §4.4
 *     (OAuth-guard), AC-7/AC-14.
 *   - lib/auth-modal (поверхность), lib/identity-events (сигнал), lib/leave-guard
 *     (OAuth-guard), services/{oauth-login,local-auth,identity}.
 */

import { openAuthModal } from './auth-modal.js';
import { emitIdentityChanged } from './identity-events.js';
import {
  bootIdentity,
  clear as clearIdentity,
} from '../services/identity.js';
import { login as loginLocal, register as registerLocal } from '../services/local-auth.js';
import { loginWithGoogle, loginWithDiscord } from '../services/oauth-login.js';
import { showToast } from '../ui/toast.js';
import { t } from './i18n/index.js';

/**
 * Открыть поверхность входа = auth-modal (§4). ОДИН модуль на все 3 вызова. Email —
 * in-place; OAuth-кнопки уводят на провайдера. На успех (email-путь):
 * `emitIdentityChanged()` → account-slot рейла + открытый Профиль reconcile'ятся.
 *
 * T-C (RP-6): OAuth-redirect больше НЕ спрашивает подтверждения (раньше leave-guard
 * confirmAbandon + подавление beforeunload). Уход на провайдера — это навигация:
 * прогресс партии уже в кэше (T-B), по возврату с OAuth «Играть» восстанавливает её.
 * `guardOAuthRedirect` всегда `true` (модалка вызывает его перед redirect — оставлен
 * для совместимости контракта auth-modal; чистка контракта — отдельный рефактор).
 */
export function openLoginFlow(): void {
  openAuthModal({
    // Тосты строятся в момент catch (показа) → t() = актуальный язык (не запекаем на import).
    onGoogle: () => loginWithGoogle().catch(() => showToast(t('auth.flow.toastGoogleFail'))),
    onDiscord: () => loginWithDiscord().catch(() => showToast(t('auth.flow.toastDiscordFail'))),
    register: registerLocal,
    login: loginLocal,
    // T-C: уход на OAuth-провайдера — тихо (партия в кэше). Без confirm.
    guardOAuthRedirect: async (): Promise<boolean> => true,
    // identity уже обновлён сервисом (local-auth) ДО колбэка → сигнал на reconcile.
    onAuthSuccess: () => emitIdentityChanged(),
  });
}

/**
 * ОДНА общая logout-функция (§3.1 / AC-14) — вызывается из меню account-chip. НЕ
 * деструктивно (Q-1): `clear()` снимает авторизованную identity → `bootIdentity()`
 * ре-минтит свежего анонима (повторный вход по email/OAuth вернёт аккаунт — анонимный
 * прогресс в браузере не теряется). Затем `emitIdentityChanged()` → account-slot →
 * пилюля «Войти», открытый Профиль → гостевой вид + конверсионный баннер (§9). БЕЗ
 * confirm-dialog (нет потери прогресса).
 */
export async function logout(): Promise<void> {
  await clearIdentity();
  await bootIdentity();
  emitIdentityChanged();
}
